import { useMemo } from 'react';
import { isReportPrivate } from '@trukt/portal-utils/isReportPrivate';
import { Download } from '@trukt/ui/icons';
import { RenderWhen } from '@trukt/ui/RenderWhen';
import { SearchExportReportsResponse } from '@api';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { PATH_NAMES } from '../../../shared';
import {
  useStartExportReportMutation,
  useCreateReportMutation,
  useDeleteReportMutation,
  useSearchExportedReportsQuery,
  useCancelExportReportMutation,
  useHandleDownloadButtonVisibility,
} from './hooks';
import { ReportSaveForm, ReportFormValues, ReportSaveFormProps } from './components';
import { ReportSaveFormContainerProps } from './ReportSaveFormContainer.types';
import { mapCreateReportRequest } from './helpers';

import {
  EXPORTING_LOADING_STATES,
  SEARCH_REPORT_EXPORTS_REFETCH_INTERVAL,
} from './ReportSaveFormContainer.constants';

import { getOptimisticReportExport } from './ReportSaveFormContainer.helpers';

export const ReportSaveFormContainer = ({
  report,
  isReportView,
  filters,
  compoundFilterState,
  getVisibleColumns,
}: ReportSaveFormContainerProps) => {
  const reportId = report?.id ?? '';

  const queryClient = useQueryClient();

  const createReportMutation = useCreateReportMutation();

  const startExportReportMutation = useStartExportReportMutation();
  const { isDownloadButtonVisible, changeDowloadButtonVisibility } =
    useHandleDownloadButtonVisibility(reportId);

  const deleteReportMutation = useDeleteReportMutation();
  const cancelExportReportMutation = useCancelExportReportMutation();

  const searchExportedReportsQuery = useSearchExportedReportsQuery(
    {
      queryParams: {
        // sending active true as we only want 1 record to be returned and that record is going to let us know if there is currently
        // a report being exported
        active: true,
      },
      routeParams: {
        // @ts-expect-error - query is disabled until reportId is truthy
        reportId: parseInt(reportId, 10),
      },
    },
    {
      refetchInterval: (query) => {
        const hasActiveExport = query.state.data?.results.some(isActiveExport);

        return hasActiveExport ? SEARCH_REPORT_EXPORTS_REFETCH_INTERVAL : false;
      },
      enabled: (query) => {
        return (
          Boolean(reportId) &&
          (query.state.data === undefined || query.state.data.results.some(isActiveExport))
        );
      },
    },
  );

  const activeReportExport = searchExportedReportsQuery.query.data?.results.find((x) =>
    EXPORTING_LOADING_STATES.includes(x.status),
  );

  const completedReportExport = searchExportedReportsQuery.query.data?.results.find(
    (x) => x.status === 'completed',
  );

  const navigate = useNavigate();

  const reportSaveFormInitialValues = useMemo<ReportFormValues | undefined>(() => {
    if (!isReportView) return undefined;

    const result: ReportFormValues = {
      isPrivate: isReportPrivate(report?.acl || 'company'),
      reportName: report?.name || '',
    };

    return result;
  }, [report, isReportView]);

  const handleSubmit: ReportSaveFormProps['onSubmit'] = ({
    values,
    triggeredAction,
    resetForm,
  }) => {
    const visibleColumns = getVisibleColumns();

    if (triggeredAction === 'save') {
      createReportMutation.mutate(
        mapCreateReportRequest({
          visibleColumns,
          compoundFilterState,
          filters,
          name: values.reportName,
        }),
        {
          onSuccess: () => {
            if (!isReportView) {
              resetForm();
            }
          },
        },
      );

      return;
    }

    if (!reportId) {
      return;
    }

    switch (triggeredAction) {
      case 'cancel-export': {
        const exportId = activeReportExport?.id;

        if (!exportId) return;

        cancelExportReportMutation.mutate(
          {
            routeParams: {
              reportId,
              exportId,
            },
          },
          {
            onSuccess: async () => {
              await queryClient.invalidateQueries({
                queryKey: searchExportedReportsQuery.queryKey,
              });
            },
          },
        );
        break;
      }
      case 'delete': {
        deleteReportMutation.mutate(
          { reportId },
          {
            onSuccess: () => {
              navigate({ to: PATH_NAMES.ROOT });
            },
          },
        );
        break;
      }
      case 'export': {
        startExportReportMutation.mutate(
          {
            routeParams: {
              reportId,
            },
            body: {
              sendEmail: true,
            },
          },
          {
            // performing an optimistic update because I don't want user to have an interrupted loading spinner experience
            onSuccess: async () => {
              await queryClient.cancelQueries({ queryKey: searchExportedReportsQuery.queryKey });

              queryClient.setQueryData<SearchExportReportsResponse>(
                searchExportedReportsQuery.queryKey,
                getOptimisticReportExport(),
              );

              await queryClient.invalidateQueries({
                queryKey: searchExportedReportsQuery.queryKey,
              });

              changeDowloadButtonVisibility(reportId, true);
            },
          },
        );

        break;
      }
    }
  };

  return (
    <>
      <RenderWhen condition={isDownloadButtonVisible}>
        <>
          {completedReportExport?.fileLocation && (
            <a download href={completedReportExport.fileLocation}>
              <Download
                role="img"
                aria-label="Download Export"
                size="1.2rem"
                className="cursor-pointer fill-primary-400 stroke-primary-400"
              >
                <title>Download Export</title>
              </Download>
            </a>
          )}
        </>
      </RenderWhen>
      <ReportSaveForm
        initialValues={reportSaveFormInitialValues}
        variant={isReportView ? 'update' : 'create'}
        onSubmit={handleSubmit}
        renderCancelExportButton={Boolean(activeReportExport?.id)}
        loadingStates={{
          isCreating: createReportMutation.isPending,
          isStartingExport: startExportReportMutation.isPending,
          isExporting: Boolean(activeReportExport),
          isCanceling: cancelExportReportMutation.isPending,
        }}
      />
    </>
  );
};

function isActiveExport(result: SearchExportReportsResponse['results'][number]) {
  return EXPORTING_LOADING_STATES.includes(result.status);
}
